import { Group, SegmentedControl, Select, Text, Tooltip } from '@mantine/core';
import { SYNC_STATUS, syncStatusOptions, type SyncStatus } from '../../domain/status.js';
import { glyphIcon } from '../StatusBadge/glyphs.js';
import type { RowFilter, RowSegment } from '../../domain/rowFilter.js';

const LEGEND: SyncStatus[] = ['drift', 'missing', 'env_only', 'in_sync', 'excluded'];

/**
 * The grid's own toolbar — the first row inside the card. Nothing here says "nothing selected".
 * The environment select (`?env=`) focuses the grid on that environment's column and scopes the
 * segment, the status and the attention count to it; the
 * URL's value is kept as an option even when no column has it, so a pasted link never reads blank.
 */
export const GridToolbar = ({
    value,
    onChange,
    attention,
    environments,
}: {
    value: RowFilter;
    onChange: (patch: Partial<RowFilter>) => void;
    attention: number;
    environments: readonly string[];
}) => (
    <Group gap="sm" wrap="wrap" p="sm" justify="space-between" style={{ borderBottom: '1px solid var(--rgs-line)' }}>
        <Group gap="sm" wrap="wrap">
            <SegmentedControl
                size="xs"
                value={value.segment}
                onChange={(segment) => onChange({ segment: segment as RowSegment })}
                data={[
                    { value: 'all', label: 'All' },
                    {
                        value: 'needs_attention',
                        label: attention > 0 ? `Needs attention ${attention}` : 'Needs attention',
                    },
                ]}
            />
            <Select
                size="xs"
                w={150}
                placeholder="Any status"
                aria-label="Filter by status"
                data={syncStatusOptions()}
                value={value.status}
                onChange={(status) => onChange({ status: (status as SyncStatus | null) ?? null })}
                clearable
            />
            <Select
                size="xs"
                w={170}
                placeholder="Any environment"
                aria-label="Environment"
                classNames={{ input: 'rgs-numeric', option: 'rgs-numeric' }}
                data={
                    value.environment && !environments.includes(value.environment)
                        ? [...environments, value.environment]
                        : [...environments]
                }
                value={value.environment}
                onChange={(environment) => onChange({ environment })}
                clearable
            />
        </Group>
        <Group gap={10} wrap="wrap" visibleFrom="sm">
            {LEGEND.map((status) => {
                const meta = SYNC_STATUS[status];
                const Glyph = glyphIcon(meta.glyph);
                return (
                    <Tooltip key={status} label={meta.description} withArrow>
                        <Group gap={3} wrap="nowrap" c={meta.color} style={{ opacity: meta.dimmed ? 0.7 : 1 }}>
                            <Glyph size={12} stroke={2.4} />
                            <Text fz={11} c="dimmed">
                                {meta.label}
                            </Text>
                        </Group>
                    </Tooltip>
                );
            })}
        </Group>
    </Group>
);
